import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { buildMeasurementPath, formatDate, getSampleIdFromSearch } from "../utils/helper"
import checkboxIcon from '../../assets/images/icons/checked_checkbox.svg'
import uncheckIcon from '../../assets/images/icons/uncheck_checkbox.svg'
import { useSpecificationDownload } from "./useSpecificationDownload"
import { getKindDisplay } from "./helpers"
import type { MeasurementReportGroup, PreviewState, PreviewTile } from "./types"
import { MeasurementReportDownloadErrorModal } from "../home/utils/MeasurementReportDownloadErrorModal"
import InfiniteScroll from "react-infinite-scroll-component"
import ReactLoading from "react-loading"
import { SamplesContext } from "../../context/samplesContext"
import { useTranslation } from "react-i18next"

const TABLE_BOX_SHADOW = { boxShadow: "0px 4px 4px 0px #00000040" }
const SAMPLES_TABLE_SCROLL_ID = "spec-download-samples-table"

export function SpecificationDownload() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const sampleIdFromSearch = getSampleIdFromSearch(location.search)
  const { sample, rowSample } = useContext(SamplesContext)
  const {
    sampleList,
    selectedGroupKeys,
    previewMap,
    sortOrder,
    changeSortOrder,
    toggleGroupSelection,
    handleDownload,
    isDownloading,
    isDownloadDisabled,
    errorModal,
    closeErrorModal,
    groupsMeta,
    loadMoreGroups
  } = useSpecificationDownload()

  const sortedSamples = useMemo(() => {
    const copy = [...sampleList]
    copy.sort((a, b) => {
      const dateA = a.created_at ? Date.parse(a.created_at) : 0
      const dateB = b.created_at ? Date.parse(b.created_at) : 0
      if (sortOrder === "asc") {
        return dateA - dateB
      }
      return dateB - dateA
    })
    return copy
  }, [sampleList, sortOrder])

  return (
    <div>
      <div className="flex items-center w-full">
        <div className="w-[8px] h-[35px] rounded-[2px] bg-[#3E3E3E] mr-[5px]" />
        <span className="text-[#3E3E3E] text-[16px]">
          {t("pages.bulkSpecificationDownload")}
        </span>
      </div>
      <div className="flex justify-center flex-col w-full laptop-md:flex-row laptop-md:mt-[10px]">
        <SamplesTable
          sortOrder={sortOrder}
          onChangeSort={changeSortOrder}
          sampleList={sortedSamples}
          selectedGroupKeys={selectedGroupKeys}
          onToggleGroupSelection={toggleGroupSelection}
          hasMore={groupsMeta.hasMore}
          onLoadMore={loadMoreGroups}
        />
        <div className="w-full mt-[10px] laptop-md:ml-[20px] laptop-md:mt-0 desktop-xxl:max-w-[1027px]">
          <Specifications
            sampleList={sampleList}
            selectedGroupKeys={selectedGroupKeys}
            previewMap={previewMap}
          />
          <div className="flex w-full justify-end mt-[20px] space-x-[14px]">
            <button
              className={'w-full max-w-[280px] min-w-[160px] h-[47px] bg-black border-[1.5px] rounded-[50px] text-[#FFF] text-[16px] hover:bg-[#303030] transition-colors'}
              onClick={() => { navigate(buildMeasurementPath(sampleIdFromSearch || rowSample.id || sample.id)) }}
            >
              {t("actions.backToMeasurement")}
            </button>
            <button
              className={`w-full max-w-[200px] min-w-[120px] h-[47px] border-[1.5px] bg-[#C5C7FF] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold transition-colors hover:bg-[#545899] hover:text-white ${isDownloadDisabled ? 'cursor-not-allowed opacity-50 hover:bg-[#C5C7FF] hover:text-[#545899]' : ''}`}
              onClick={handleDownload}
              disabled={isDownloadDisabled}
            >
              {isDownloading ? t("common.downloading") : t("common.download")}
            </button>
          </div>
        </div>
      </div>
      <MeasurementReportDownloadErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        message={errorModal.message}
      />
    </div>
  )
}

function SamplesTable(props: {
  sampleList: MeasurementReportGroup[]
  sortOrder: "desc" | "asc"
  onChangeSort: (order: "desc" | "asc") => void
  selectedGroupKeys: string[]
  onToggleGroupSelection: (groupKey: string) => void
  hasMore: boolean
  onLoadMore: () => void
}) {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith("en")
  const titleTextClass = isEnglish ? "text-[11px] leading-[1.15] whitespace-nowrap" : "text-[16px]"
  const sortGroupClass = isEnglish ? "flex items-center space-x-[8px]" : "flex items-center space-x-[16px]"
  const sortLabelClass = isEnglish ? "flex items-center space-x-[5px] text-[11px] leading-[1.15] whitespace-nowrap" : "flex items-center space-x-[6px]"
  const sortRadioClass = isEnglish ? "w-[22px] h-[22px] accent-[#545899]" : "w-[26px] h-[26px] accent-[#545899]"
  const tableHeaderTextClass = isEnglish ? "text-[12px] leading-[1.1]" : "text-[14px]"
  const {
    sampleList,
    sortOrder,
    onChangeSort,
    selectedGroupKeys,
    onToggleGroupSelection,
    hasMore,
    onLoadMore,
  } = props

  return (
    <div
      className="w-[687px] min-w-[651px] h-[420px] bg-white rounded-[20px] pt-[20px] pb-[22px] px-[27px] shadow-[0px_4px_4px_0px_#00000040] max-laptop-md:bg-transparent max-laptop-md:shadow-none max-laptop-md:w-full laptop-md:h-[750px]"
    >
      <div className="flex items-center justify-between w-full pb-[10px]">
        <span className={`${titleTextClass} max-laptop-md:opacity-0`}>{t("pages.specificationHistory")}</span>
        <div className={sortGroupClass}>
          <label className={sortLabelClass}>
            <input
              type="radio"
              name="sort"
              value="asc"
              checked={sortOrder === "asc"}
              onChange={() => onChangeSort("asc")}
              className={sortRadioClass}
            />
            <span>{t("actions.measurementDateAsc")}</span>
          </label>
          <label className={sortLabelClass}>
            <input
              type="radio"
              name="sort"
              value="desc"
              checked={sortOrder === "desc"}
              onChange={() => onChangeSort("desc")}
              className={sortRadioClass}
            />
            <span>{t("actions.measurementDateDesc")}</span>
          </label>
        </div>
      </div>
      <div className="h-[370px] laptop-md:h-[662px] overflow-y-scroll border border-gray-400" id={SAMPLES_TABLE_SCROLL_ID}>
        <InfiniteScroll
          dataLength={sampleList.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
              <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
              <span>{t("messages.loadingSpecificationHistory")}</span>
            </div>
          }
          endMessage={<div className="py-3 text-center text-xs text-[#666666]">{t("messages.allSpecificationHistoryLoaded")}</div>}
          scrollableTarget={SAMPLES_TABLE_SCROLL_ID}
          scrollThreshold="200px"
          style={{ overflow: "visible" }}
        >
          <table className="w-full">
            <thead className="bg-[#3E3E3E] h-[30px]">
              <tr className={`sticky inset-0 bg-[#3E3E3E] text-white ${tableHeaderTextClass}`}>
                <th className="sticky top-0 w-[77px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none"></th>
                <th className="sticky top-0 w-[135px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none">{t("sample.measurementDate")}</th>
                <th className="sticky top-0 w-[115px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none">{t("sample.partNumber")}</th>
                <th className="sticky top-0 w-[115px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none">{t("sample.sampleNumber")}</th>
                <th className="sticky top-0 w-[100px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none">{t("sample.sampleStatus")}</th>
                <th className="sticky top-0 w-[75px] font-normal before:absolute before:inset-0 before:border before:border-gray-400 before:content-[''] before:pointer-events-none">{t("sample.size")}</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sampleList.map(item => {
                const isSelected = selectedGroupKeys.includes(item.group_key)
                const sizeDisplay = item.size_values.length > 0 ? item.size_values.join(", ") : "-"

                return (
                  <tr key={item.group_key} className="text-[14px]">
                    <td className="h-[30px] border border-l-0 border-t-0 border-gray-400 text-center">
                      <button
                        type="button"
                        aria-label={t("accessibility.selectItem", { label: item.display_label })}
                        onClick={() => onToggleGroupSelection(item.group_key)}
                        className="h-full inline-flex items-center justify-center"
                      >
                        <img
                          src={isSelected ? checkboxIcon : uncheckIcon}
                          alt={isSelected ? t("accessibility.checkedCheckbox") : t("accessibility.uncheckedCheckbox")}
                          className="w-[20px] h-[20px]"
                        />
                      </button>
                    </td>
                    <td className="border border-t-0 border-gray-400 text-center text-[14px]">
                      {item.created_at ? formatDate(item.created_at) : "-"}
                    </td>
                    <td className="border border-t-0 border-gray-400 text-center text-[14px]">{item.part_number || "-"}</td>
                    <td className="border border-t-0 border-gray-400 text-center text-[14px]">{item.sample_number || "-"}</td>
                    <td className="border border-t-0 border-gray-400 text-center text-[14px]">{getKindDisplay(item)}</td>
                    <td className="border border-t-0 border-gray-400 text-center text-[14px]">{sizeDisplay}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}

function Specifications(props: {
  sampleList: MeasurementReportGroup[]
  selectedGroupKeys: string[]
  previewMap: Record<string, PreviewState>
}) {
  const { t } = useTranslation()
  const { sampleList, selectedGroupKeys, previewMap } = props

  const selectedItems = useMemo(() => {
    return selectedGroupKeys
      .map(key => sampleList.find(item => item.group_key === key))
      .filter((item): item is MeasurementReportGroup => Boolean(item))
  }, [sampleList, selectedGroupKeys])

  const displayTiles = useMemo((): PreviewTile[] => {
    return selectedGroupKeys.flatMap<PreviewTile>(groupKey => {
      const state = previewMap[groupKey]
      if (state && state.status === "success") {
        return state.data.images.map<PreviewTile>(image => ({
          kind: "image" as const,
          id: `${groupKey}-${image.pageNumber}`,
          mimeType: image.mimeType,
          base64: image.base64
        }))
      }
      if (state && state.status === "error") {
        return [{
          kind: "error" as const,
          id: `${groupKey}-error`,
          message: state.errorMessage ?? t("messages.previewFetchFailed")
        }]
      }
      return [{
        kind: "loading" as const,
        id: `${groupKey}-loading`
      }]
    })
  }, [selectedGroupKeys, previewMap, t])

  const isLoading = useMemo(
    () => displayTiles.some(tile => tile.kind === "loading"),
    [displayTiles]
  )



  const [erroredImages, setErroredImages] = useState<Set<string>>(new Set())

  const handleImageError = useCallback((id: string) => {
    setErroredImages(prev => {
      if (prev.has(id)) {
        return prev
      }
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  useEffect(() => {
    setErroredImages(prev => {
      const next = new Set<string>()
      displayTiles.forEach(tile => {
        if (tile.kind === "image" && prev.has(tile.id)) {
          next.add(tile.id)
        }
      })
      return next
    })
  }, [displayTiles])

  return (
    <div
      className="flex-1 h-[688px] p-[20px] pr-[10px] pb-[15px] bg-white rounded-[20px]"
      style={TABLE_BOX_SHADOW}
    >
      <div
        className="h-full pr-[10px] pb-[5px] overflow-y-auto grid grid-cols-1 gap-[10px] content-start items-start desktop-md:grid-cols-2 desktop-lg:grid-cols-3 desktop-xl:grid-cols-4 max-laptop-md:grid-cols-3 max-tablet-lg:grid-cols-2"
      >
        {selectedItems.length === 0 ? (
          <div></div>
        ) : (
          <>
            {displayTiles.length > 0 ? (
              <>
                {displayTiles.map(tile => {
                  if (tile.kind === "image") {
                    const isErrored = erroredImages.has(tile.id)
                    return (
                      <div
                        key={tile.id}
                        className="flex items-center justify-center w-full h-[220px] border border-gray-300 rounded bg-[#FFF] desktop-md:h-[200px] desktop-lg:h-[180px] desktop-xl:h-[154px]"
                      >
                        {isErrored ? (
                          <div className="flex h-full w-full flex-col items-center justify-center rounded bg-[#EFEFEF] text-[12px] text-[#5A5A5A]">
                            <span>{t("messages.previewLoadFailed")}</span>
                          </div>
                        ) : (
                          <img
                            src={`data:${tile.mimeType};base64,${tile.base64}`}
                            alt={t("accessibility.previewImage")}
                            className="h-full w-full object-contain"
                            onError={() => handleImageError(tile.id)}
                          />
                        )}
                      </div>
                    )
                  }

                  if (tile.kind === "error") {
                    return (
                      <div
                        key={tile.id}
                        className="flex items-center justify-center w-full h-[220px] border border-gray-300 rounded bg-[#FFEAEA] text-[12px] text-red-600 desktop-md:h-[200px] desktop-lg:h-[180px] desktop-xl:h-[154px]"
                      >
                        <span>{tile.message}</span>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={tile.id}
                      className="flex flex-col w-full h-[220px] items-center justify-center border border-dashed border-gray-300 rounded bg-[#F7F7F7] desktop-md:h-[200px] desktop-lg:h-[180px] desktop-xl:h-[154px]"
                    >
                      <ReactLoading type="spin" color="#5A5A5A" height={24} width={24} className="inline-block mr-2" />
                      <span className="text-[12px] text-[#5A5A5A] mt-4">{t("messages.preparingPreview")}</span>
                    </div>
                  )
                })}
              </>
            ) : (
              !isLoading && (
                <div className="text-[12px] text-[#5A5A5A]">{t("messages.noPreview")}</div>
              )
            )}
          </>
        )}
      </div>
    </div>
  )
}
